/* eslint-disable react/destructuring-assignment */

'use client';

import { Link } from '@/i18n/navigation';
import cn from 'classnames';
import { CSSProperties, ReactElement } from 'react';

import type { ButtonProps, LinkButtonProps, RegularButtonProps } from './Button.types';

import s from './Button.module.css';

const isLinkButton = (props: LinkButtonProps | RegularButtonProps): props is LinkButtonProps => {
  return props.variant === 'link';
};

const Button = (props: ButtonProps): ReactElement => {
  const {
    children,
    icon,
    width,
    disabled,
    spacing,
    className,
    href,
    isFullWidth = false,
    iconPosition = 'leading',
    type = 'button',
    onlyIcon = false,
    variant = 'primary',
    isFullRadius,
    onClick,
    ...rest
  } = props;

  const buttonStyles: CSSProperties = {
    width: width && !isFullWidth ? `${width}px` : undefined,
  };
  const size = !isLinkButton(props) && props.size ? props.size : 'medium';
  const actionRole = isLinkButton(props) ? props.actionRole ?? 'action' : undefined;
  const sizeClass = !isLinkButton(props) ? s[size] : '';
  const actionRoleClass = actionRole ? s[actionRole] : '';

  const buttonClass = cn(s.button, className, s[variant], actionRoleClass, {
    [s.withIcon]: !!icon,
    [s.onlyIcon]: onlyIcon,
    [s.fullWidth]: !!isFullWidth,
    [s.isFullRadius]: isFullRadius,
    [sizeClass]: !isLinkButton(props),
    [`spacing-${spacing}`]: !!spacing,
  });

  const buttonContent = (
    <>
      {iconPosition === 'leading' && icon}
      {!onlyIcon && children && children}
      {iconPosition === 'trailing' && icon}
    </>
  );

  if (href) {
    return (
      <Link href={href} className={buttonClass} style={buttonStyles} {...rest}>
        {buttonContent}
      </Link>
    );
  }

  const buttonProps = {
    type,
    className: buttonClass,
    style: buttonStyles,
    onClick,
    disabled,
    ...rest,
  };

  return <button {...buttonProps}>{buttonContent}</button>;
};

export default Button;
